import type { Id } from "@repo/convex/dataModel";
import { Check, Ellipsis, Folder, FolderInput, LayoutGrid, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMoveFolder, useRemoveFolder } from "@/features/project/hooks";
import { DeleteFolderDialog } from "./delete-folder-dialog";

export interface FlatFolderTarget {
  _id: Id<"projectFolders">;
  name: string;
}

interface FolderContextMenuProps {
  folderId: Id<"projectFolders">;
  folderName: string;
  currentParentFolderId?: Id<"projectFolders">;
  validMoveTargets: FlatFolderTarget[];
  projectCount: number;
  childFolderCount: number;
  onRenameStart: () => void;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children: React.ReactNode;
}

/**
 * Wraps children with a right-click context menu for folder actions.
 * Renders: Rename, Move to… (submenu excluding self + descendants), Delete (with cascade confirmation).
 */
export function FolderContextMenu({
  folderId,
  folderName,
  currentParentFolderId,
  validMoveTargets,
  projectCount,
  childFolderCount,
  onRenameStart,
  onOpenChange,
  className,
  children,
}: FolderContextMenuProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const moveFolder = useMoveFolder();
  const removeFolder = useRemoveFolder();

  const handleMove = async (parentFolderId?: Id<"projectFolders">) => {
    try {
      await moveFolder({ folderId, parentFolderId });
      toast.success(parentFolderId ? "Folder moved" : "Folder moved to root");
    } catch {
      toast.error("Failed to move folder");
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteOpen(false);
      await removeFolder({ folderId });
      toast.success("Folder deleted");
    } catch {
      toast.error("Failed to delete folder");
    }
  };

  return (
    <>
      <ContextMenu onOpenChange={onOpenChange}>
        <ContextMenuTrigger className={className}>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-[168px]">
          <ContextMenuItem onClick={onRenameStart}>
            <Pencil className="mr-2 size-3.5" />
            Rename
          </ContextMenuItem>
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <FolderInput className="mr-2 size-3.5" />
              Move to...
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem
                className={!currentParentFolderId ? "text-muted-foreground" : undefined}
                disabled={!currentParentFolderId}
                onClick={() => void handleMove(undefined)}
              >
                <LayoutGrid className="mr-2 size-3.5 text-neutral-dim" />
                Projects
                <Check
                  className={`ml-auto size-3 ${!currentParentFolderId ? "text-muted-foreground" : "invisible"}`}
                />
              </ContextMenuItem>
              {validMoveTargets.length > 0 ? <ContextMenuSeparator /> : null}
              {validMoveTargets.map((f) => (
                <ContextMenuItem
                  key={String(f._id)}
                  className={f._id === currentParentFolderId ? "text-muted-foreground" : undefined}
                  disabled={f._id === currentParentFolderId}
                  onClick={() => void handleMove(f._id)}
                >
                  <Folder className="mr-2 size-3.5 text-neutral-dim" />
                  {f.name}
                  <Check
                    className={`ml-auto size-3 ${f._id === currentParentFolderId ? "text-muted-foreground" : "invisible"}`}
                  />
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuSeparator />
          <ContextMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="mr-2 size-3.5" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <DeleteFolderDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        folderName={folderName}
        projectCount={projectCount}
        childFolderCount={childFolderCount}
        onConfirm={() => void handleDelete()}
      />
    </>
  );
}

interface FolderOverflowMenuProps {
  folderId: Id<"projectFolders">;
  folderName: string;
  currentParentFolderId?: Id<"projectFolders">;
  validMoveTargets: FlatFolderTarget[];
  projectCount: number;
  childFolderCount: number;
  onRenameStart: () => void;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * Overflow "..." button with dropdown menu for folder actions.
 * Same actions as FolderContextMenu but triggered by clicking a button.
 */
export function FolderOverflowMenu({
  folderId,
  folderName,
  currentParentFolderId,
  validMoveTargets,
  projectCount,
  childFolderCount,
  onRenameStart,
  onOpenChange,
  className,
}: FolderOverflowMenuProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const moveFolder = useMoveFolder();
  const removeFolder = useRemoveFolder();

  const handleMove = async (parentFolderId?: Id<"projectFolders">) => {
    try {
      await moveFolder({ folderId, parentFolderId });
      toast.success(parentFolderId ? "Folder moved" : "Folder moved to root");
    } catch {
      toast.error("Failed to move folder");
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteOpen(false);
      await removeFolder({ folderId });
      toast.success("Folder deleted");
    } catch {
      toast.error("Failed to delete folder");
    }
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger render={<span />}>
          <DropdownMenu onOpenChange={onOpenChange}>
            <DropdownMenuTrigger
              className={className}
              onClick={(e) => e.stopPropagation()}
              render={
                <button
                  type="button"
                  aria-label={`Actions for ${folderName}`}
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground"
                />
              }
            >
              <Ellipsis className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={4} className="w-[168px]">
              <DropdownMenuItem onClick={onRenameStart}>
                <Pencil className="mr-2 size-3.5" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <FolderInput className="mr-2 size-3.5" />
                  Move to...
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    className={!currentParentFolderId ? "text-muted-foreground" : undefined}
                    disabled={!currentParentFolderId}
                    onClick={() => void handleMove(undefined)}
                  >
                    <LayoutGrid className="mr-2 size-3.5 text-neutral-dim" />
                    Projects
                    <Check
                      className={`ml-auto size-3 ${!currentParentFolderId ? "text-muted-foreground" : "invisible"}`}
                    />
                  </DropdownMenuItem>
                  {validMoveTargets.length > 0 ? <DropdownMenuSeparator /> : null}
                  {validMoveTargets.map((f) => (
                    <DropdownMenuItem
                      key={String(f._id)}
                      className={
                        f._id === currentParentFolderId ? "text-muted-foreground" : undefined
                      }
                      disabled={f._id === currentParentFolderId}
                      onClick={() => void handleMove(f._id)}
                    >
                      <Folder className="mr-2 size-3.5 text-neutral-dim" />
                      {f.name}
                      <Check
                        className={`ml-auto size-3 ${f._id === currentParentFolderId ? "text-muted-foreground" : "invisible"}`}
                      />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="mr-2 size-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipTrigger>
        <TooltipContent side="bottom">More actions</TooltipContent>
      </Tooltip>
      <DeleteFolderDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        folderName={folderName}
        projectCount={projectCount}
        childFolderCount={childFolderCount}
        onConfirm={() => void handleDelete()}
      />
    </>
  );
}
